'use strict';

import React, {Component} from 'react';
import PropTypes from "prop-types";
import {observer} from "mobx-react";
import Loader from "@uw-it-sis/lib-react/lib/Loader";
import moment from "moment";
import classnames from "classnames";
import {omit} from "underscore";

import {FaLock, FaExclamationTriangle, FaList, FaRegCalendarAlt} from "react-icons/fa";

import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import bootstrap5Plugin from "@fullcalendar/bootstrap5";
import interactionPlugin from "@fullcalendar/interaction";

import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import Badge from "react-bootstrap/Badge";

import SimpleState from "@uw-it-sis/lib-react/lib/SimpleState";
import CarouselMulti from "@uw-it-sis/lib-react/lib/CarouselMulti";
import {States, Breakpoints} from "@uw-it-sis/lib-react/lib/AppConstants";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";

import ScheduleBuilderOption from "./ScheduleBuilderOption";
import scheduleBuilderOptionsStore from './ScheduleBuilderOptionsStore';
import scheduleBuilderStore from './ScheduleBuilderStore';
import ScheduleActivityDetailModal from './ScheduleActivityDetailModal';
import ScheduleBuilderOptionsTBA from "./ScheduleBuilderOptionsTBA";
import ScheduleBuilderOptionsList from "./ScheduleBuilderOptionsList";
import {getCalendarProps} from "./ScheduleBuilderUtils";
import PinnedSchedulesStore from "./PinnedSchedulesStore";

class ScheduleBuilderOptions extends Component {
    constructor(props) {
        super(props);
        this.props = props;

        this.store = scheduleBuilderOptionsStore;

        this.width = window.innerWidth;

        //state for showing the courseModal and for passing the event data to it
        this.state = {
            showCourseModal: false,
            event: []
        };

        this.closeDisplayLimitModal = this.closeDisplayLimitModal.bind(this);
        this.buildDoneComponent = this.buildDoneComponent.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    componentDidMount() {
        this.store.mount(this.props.termId);
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize() {
        let newWidth = window.innerWidth;
        let mdWidth = Breakpoints.md.width;
        let lgWidth = Breakpoints.lg.width;
        if (
            (newWidth < mdWidth && this.width >= mdWidth)
            ||
            (newWidth >= mdWidth && this.width < mdWidth)
            ||
            (newWidth < lgWidth && this.width >= lgWidth)
            ||
            (newWidth >= lgWidth && this.width < lgWidth)
        ) {
            this.width = newWidth;
            this.store.mount(this.props.termId);
        }
    }

    componentDidUpdate(prevProps) {
        if ( this.props.termId !== prevProps.termId ) {
            this.store.mount(this.props.termId);
       }
    }

    closeDisplayLimitModal() {
        this.store.closeDisplayLimitMessage();
    }

    /**
     * toggle the state(open/close) of the courseModal
     */
    toggle = () => {
        this.setState({ showCourseModal: !this.state.showCourseModal });

        // if the new state is closed, reset the modal data
        if (!this.state.showCourseModal) {
            this.setState( {event: {}});
        }
    };

    /**
     * Handler  for the click event of the scheduled course option
     * @param event
     * @param el
     */
    handleEventClick = (eventClickInfo) => {
        let event = eventClickInfo.event;
        if (!event.extendedProps.reserved) {
            this.toggle();
            this.setState({ event: event });
        }
    };

    buildDoneComponent() {
        let possibleScheduleNav, scheduleOptions, schedulesCalendar, schedulesTBA, registeredLegend, cardBody, errorMessages;

        let scheduleOptionsCount = "Loading..."

        let isPending = this.store.state === States.pending;

        const loaderSpinner = <Spinner
            as="span"
            animation="border"
            role="status"
            aria-hidden="true"
        />;

        // Modal for informing the display limit on schedule options
        let displayLimitMessage =
            <Modal show={this.store.showDisplayLimitMessage}
                   onHide={this.closeDisplayLimitModal}
                   aria-labelledby="max-schedules-displayed-modal"
                   centered>
                <Modal.Header closeButton>
                    <Modal.Title id="max-schedules-displayed-modal">Maximum number of schedules shown</Modal.Title>
                </Modal.Header>
                <Modal.Body>You can display up to {this.store.eventsLimitIndicator} possible schedules at a time in the calendar grid below.
                    To add this one, you'll first need to deselect one of the schedules that's currently
                    displayed.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={this.closeDisplayLimitModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>;

        let courseModal;
        // Build the course modal
        if (this.state.event.extendedProps) {
            courseModal =
                <ScheduleActivityDetailModal activity={this.state.event.extendedProps.courseObj}
                                             indicator={this.state.event.extendedProps.indicator}
                                             registered={this.state.event.extendedProps.registered}
                                             onHide={this.toggle}
                                             show={this.state.showCourseModal}
                                             planData={this.store.planData} />
        }

        // If there are possible schedule options
        let showPossibleSchedules = this.store.schedules !== null && this.store.schedules.hasOwnProperty("possibleSchedules") && this.store.schedules.possibleSchedules.length > 0;

        if (this.store.schedules !== null && this.store.schedules.hasOwnProperty("possibleSchedules")) {
            // Navigation for switching between calendar and list view
            possibleScheduleNav =
                <Nav>
                    <Nav.Item>
                        <Nav.Link eventKey="calendar" aria-label="Show Calendar View">
                            <FaRegCalendarAlt aria-hidden="true" className="me-sm-2" />
                            <span className="d-none d-sm-inline">Calendar <span className="d-none d-md-inline">view</span></span>
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="list" aria-label="Show List View">
                            <FaList aria-hidden="true" className="me-sm-2" />
                            <span className="d-none d-sm-inline">List <span className="d-none d-md-inline">view</span></span>
                        </Nav.Link>
                    </Nav.Item>
                </Nav>;

            // Array of combined possible schedules and pinned options, used for calculating full calendar props
            const mergedScheduleAndPinnedOptions = this.store.schedules.possibleSchedules.concat(PinnedSchedulesStore.pinnedSchedules);

            // Full Calendar props
            let calendarProps = Object.assign({
                    themeSystem: "bootstrap5",
                    plugins: [timeGridPlugin, bootstrap5Plugin, interactionPlugin],
                    editable: true,
                    height: "auto",
                    allDaySlot: false,
                    eventStartEditable: false,
                    eventDurationEditable: false,
                    timeZone: "America/Los_Angeles",
                    headerToolbar: {start: '', center: '', end: ''},
                    dayHeaderFormat: {weekday: (window.innerWidth < Breakpoints.md.width ? 'short':'long')},
                    initialView: "timeGridWeek",
                    initialDate: scheduleBuilderStore.getInitialDate(),
                    slotEventOverlap: false,
                    displayEventTime: false,
                    hiddenDays: [0, 6], // Default hide weekends on calendar
                },
                getCalendarProps(mergedScheduleAndPinnedOptions)
            );

            // Sets the display contents of events on the calendar
            const eventContent = (arg) => {
                let start = moment(arg.event.startStr);
                let end = moment(arg.event.endStr);
                let accessibleText = <div className="sr-only">{start.format('dddd h:mm a') + ' to ' + end.format('h:mm a')}</div>;

                // schedule option events
                if (arg.event.extendedProps.indicator) {
                    // Event badges
                    let eventBadges;
                    if (arg.event.extendedProps.hasOwnProperty("badges") && arg.event.extendedProps.badges.length > 0) {
                        eventBadges = arg.event.extendedProps.badges.map((badge, i) => { return (
                            <Badge key={i} pill bg="white" className="text-dark text-uppercase fs-10 text-nowrap m-0 me-2">{badge}</Badge>
                        )});
                    }

                    return (
                        <div className={classnames("px-1 pb-1")}>
                            <div className="lead lh-sm"><strong><span className="sr-only">Schedule Option </span>{arg.event.extendedProps.indicator}</strong></div>
                            <div className="text-truncate">{arg.event.title}</div>
                            <div>
                                {eventBadges}
                            </div>
                            {accessibleText}
                        </div>
                    );
                } else {
                    // registered course events
                    if (arg.event.extendedProps.registered) {
                        // Event badges
                        let eventBadges;
                        if (arg.event.extendedProps.hasOwnProperty("badges") && arg.event.extendedProps.badges.length > 0) {
                            eventBadges = arg.event.extendedProps.badges.map((badge, i) => { return (
                                <div key={i}><Badge pill bg="gray-dark" className="text-uppercase fs-10">{badge}</Badge></div>
                            )});
                        }

                        return (
                            <div className={classnames("pt-1 px-1 pb-1")}>
                                <div className="text-muted">
                                    <FaLock aria-hidden="true" />
                                    <span className="sr-only">Registered Course</span>
                                </div>
                                <div className="text-truncate text-muted">{arg.event.title}</div>
                                {eventBadges}
                                {accessibleText}
                            </div>
                        );
                    }
                    // reserved times events
                    return (
                        <div>
                            <div className="sr-only">{arg.event.title}</div>
                            {accessibleText}
                        </div>
                    );
                }
            }

            const eventOrder = (a, b) => {
                if (a.extendedProps.hasOwnProperty("indicator") && b.extendedProps.hasOwnProperty("indicator")) {
                    if (a.extendedProps.indicator < b.extendedProps.indicator) return -1;
                    if (a.extendedProps.indicator > b.extendedProps.indicator) return 1;
                }

                if (a.extendedProps.hasOwnProperty("indicator") && !b.extendedProps.hasOwnProperty("indicator")) {
                    return -1;
                }

                if (!a.extendedProps.hasOwnProperty("indicator") && b.extendedProps.hasOwnProperty("indicator")) {
                    return 1;
                }

                return 0;
            }

            const eventDidMount = (eventClickInfo) => {
                let element = eventClickInfo.el
                element.setAttribute('tabindex', '0');
                element.onkeypress = (e) => {
                    if ([13, 32].includes(e.which)) {
                        e.preventDefault();
                        this.handleEventClick(eventClickInfo);
                    }
                };
            }

            if ( this.store.hasAcademicRecordItems() ) {
                registeredLegend =
                    <Row className="mt-3" style={{marginLeft: '29px'}}>
                        <Col>
                            <FaLock className="text-muted me-2 align-middle fs-7" aria-hidden="true" />
                            <small>Registered Courses</small>
                        </Col>
                    </Row>;
            }

            // Do any options contain weekend (for Saturday classes), if true, then we only hide Sunday on the calender grid
            let hasWeekends = ( calendarProps.hasWeekends || this.store.hasAcademicRecordWeekends() );
            if (hasWeekends) {
                calendarProps.hiddenDays = [0];
            }

            if (showPossibleSchedules) {
                scheduleOptionsCount = this.store.schedules.possibleSchedules.length;

                let schedulesList = this.store.schedules.possibleSchedules.map((schedule, index) => {
                    return <ScheduleBuilderOption schedule={schedule}
                                                  initialDate={calendarProps.initialDate}
                                                  key={index}
                                                  defaultShow={index === 0}
                                                  resultIndex={index + 1}
                                                  regDate={this.props.regDate}
                                                  planData={this.store.planData}  />;
                });

                scheduleOptions =
                    <CarouselMulti xs={2} sm={3} md={4} lg={5} xl={5}
                                   indicators={false}
                                   wrap={true}
                                   interval={null}
                                   className="scheduleBuilder-carousel carousel-gray-arrows mb-3"
                                   aria-live="polite">
                        {schedulesList}
                    </CarouselMulti>;
            } else {
                scheduleOptionsCount = 0;
                errorMessages = this.store.getNoPossibleScheduleMsg().map((message, index) => {
                    return <li key={index} dangerouslySetInnerHTML={{__html: message}}/>;
                });
            }

            schedulesCalendar =
                <FullCalendar
                    eventContent={eventContent}
                    eventOrder={["start", "-duration", eventOrder, "title"]}
                    eventClick={this.handleEventClick}
                    eventDidMount={eventDidMount}
                    eventSources={this.store.displayedEvents.slice()}
                    {...omit(calendarProps, "hasWeekends")}
                />;

            schedulesTBA = <ScheduleBuilderOptionsTBA eventSources={this.store.displayedEvents.slice()}
                                                      eventClick={this.handleEventClick}
                                                      width={hasWeekends ? 6 : 5} />;
        }

        // Check displayed events for any schedule or pinned options being display, if there are none, set flag true
        let hasNoDisplayedScheduleOptions = (this.store.displayedEvents.filter(eventObject => eventObject.hasOwnProperty("isScheduleOption") && eventObject.isScheduleOption).length === 0);

        let calendarHeading, calendarIntro;
        if (this.store.showRegisteredScheduleCalendar()) {
            calendarHeading = "Registered Schedule";
            calendarIntro = <p>The following classes are registered for {TermUtils.makeTermDescriptionFromTermId(this.props.termId)}</p>;
        } else {
            calendarHeading = `Possible Schedules: ${scheduleOptionsCount}`;
            calendarIntro = <p>Select schedule options below to view and compare schedules. You can compare {this.store.eventsLimitIndicator} schedule options at a
                time in the calendar view. Selecting a course will show the sections.</p>
        }

        cardBody =
            <>
                {calendarIntro}
                {scheduleOptions}
                <Tab.Content className="p-0">
                    <Tab.Pane eventKey="calendar">
                        <div className="scheduleBuilder-calendar">
                            {schedulesCalendar}
                            {schedulesTBA}
                            {registeredLegend}
                        </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey="list">
                        <ScheduleBuilderOptionsList eventSources={this.store.displayedEvents.slice()} eventClick={this.handleEventClick} />
                    </Tab.Pane>
                </Tab.Content>
                {courseModal}
                {displayLimitMessage}
            </>;

        if (!showPossibleSchedules && hasNoDisplayedScheduleOptions && !isPending && !this.store.showRegisteredScheduleCalendar()) {
            cardBody =
                <div className="p-3 d-flex align-items-start">
                    <FaExclamationTriangle aria-hidden="true" className="text-warning me-3 mt-1 fs-5" />
                    <div>
                        <p className="lead"><strong>No schedule options available with your selection.</strong></p>
                        <ul>
                            {errorMessages}
                        </ul>
                        <p><Button variant="inline-link" onClick={() => {
                            const element = document.getElementById("sb-selections");
                            element.scrollIntoView(true);
                        }}>Adjust the selected courses</Button> and/or filters above.</p>
                        <p className="mb-0"><a
                            href="https://itconnect.uw.edu/learn/tools/myplan-academic-planner/schedule-builder/#troubleshooting"
                            target="_blank">View tips for troubleshooting 0 schedule options</a> and try again.</p>
                    </div>
                </div>;
        }

        return (
            <div className="scheduleBuilder-options">
                <Loader show={isPending}
                        message={loaderSpinner}
                        disableDefaultStyles>
                    <Tab.Container defaultActiveKey="calendar" transition={false} mountOnEnter unmountOnExit>
                        <Card border="border" role="region" aria-label="Schedule Builder Results">
                            <Card.Header className="d-flex align-items-center justify-content-between py-2 bg-navigate">
                                <h3 className="h4 mb-0" aria-live="polite" aria-atomic="true">{calendarHeading}</h3>
                                {possibleScheduleNav}
                            </Card.Header>
                            <Card.Body>
                                {cardBody}
                            </Card.Body>
                        </Card>
                    </Tab.Container>
                </Loader>
            </div>
        );
    }

    render() {
        // Use 'dots' animation for first time load of course section data
        let pendingComponent = "dots";
        // If data is already present...
        if ( this.store.hasData() ) {
            pendingComponent = this.buildDoneComponent;
        }

        return (
            <SimpleState state={this.store.state}
                         doneComponentBuilder={this.buildDoneComponent}
                         pendingComponentBuilder={pendingComponent}
                         name="ScheduleBuilderOptions"/>
        )
    }
}

ScheduleBuilderOptions.propTypes = {
    termId: PropTypes.string.isRequired,
    regDate: PropTypes.string.isRequired
}

export default observer(ScheduleBuilderOptions);